"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.FakeCollectionsClient = void 0;
const test_support_1 = require("../../../test-support");
const VideosFactory_1 = require("../../../test-support/VideosFactory");
const Attachment_1 = require("../../common/model/Attachment");
const AttachmentsFactory_1 = require("../../../test-support/AttachmentsFactory");
const LinkEntity_1 = require("../../common/model/LinkEntity");
const CollectionUpdateConflictError_1 = require("./CollectionUpdateConflictError");
const CollectionPermissions_1 = require("../model/CollectionPermissions");
const BoclipsApiErrorFactory_1 = require("../../../test-support/BoclipsApiErrorFactory");
class FakeCollectionsClient {
    constructor() {
        this.collections = [];
        this.nextId = '123';
        this.validReferers = [];
    }
    static mapUpdateRequestToFields(request, assets) {
        const partialCollection = { assets: assets };
        if (request.hasOwnProperty('title')) {
            partialCollection.title = request.title;
        }
        if (request.hasOwnProperty('discoverable')) {
            partialCollection.discoverable = request.discoverable;
        }
        if (request.hasOwnProperty('subjects')) {
            partialCollection.subjects = request.subjects?.map((id) => test_support_1.SubjectFactory.sample({ id }));
        }
        if (request.hasOwnProperty('promotedFor')) {
            partialCollection.promotedFor = request.promotedFor;
        }
        if (request.hasOwnProperty('description')) {
            partialCollection.description = request.description;
        }
        if (request.hasOwnProperty('videos')) {
            partialCollection.assets = request.videos?.map((id) => test_support_1.CollectionAssetFactory.sample({
                id: { videoId: id, highlightId: null },
                video: VideosFactory_1.VideoWithBoclipsProjectionFactory.sample({ id }),
            }));
        }
        if (request.hasOwnProperty('assets')) {
            partialCollection.assets = request.assets?.map((asset) => test_support_1.CollectionAssetFactory.sample({
                id: {
                    videoId: asset.videoId,
                    highlightId: asset.highlightId,
                },
                segment: asset.segment,
                note: asset.note,
                video: VideosFactory_1.VideoWithBoclipsProjectionFactory.sample({
                    id: asset.videoId,
                }),
            }));
        }
        if (request.hasOwnProperty('attachment')) {
            const attachmentType = request.attachment?.type
                ? (0, Attachment_1.getAttachmentType)(request.attachment.type)
                : undefined;
            if (attachmentType === undefined) {
                throw new Error(`${request.attachment?.type} is not a valid attachment type`);
            }
            partialCollection.attachments = [
                AttachmentsFactory_1.AttachmentFactory.sample({
                    type: attachmentType,
                    description: request.attachment?.description,
                    linkToResource: request.attachment?.linkToResource,
                }),
            ];
        }
        if (request.hasOwnProperty('ageRange')) {
            const min = request.ageRange?.min;
            const max = request.ageRange?.max;
            partialCollection.ageRange = {
                ...request.ageRange,
                label: min ? (max ? `${min}-${max}` : `${min}+`) : '',
            };
        }
        return partialCollection;
    }
    get(id, _projection, referer) {
        const collection = this.convertDateIfHasComments(this.collections.find((c) => c.id === id)) || null;
        return !referer || this.validReferers.includes(referer)
            ? Promise.resolve(collection)
            : Promise.reject(BoclipsApiErrorFactory_1.BoclipsApiErrorFactory.sample({ status: 403 }));
    }
    getCollections(filter, isOwnCollection, createdByBoclips) {
        const page = filter.page || 0;
        const size = filter.size || 10;
        const fromIndex = page * size;
        const untilIndex = (page + 1) * size;
        const filteredCollections = this.getFilteredCollections(filter.query, filter.partialTitleMatch, isOwnCollection, filter.canEdit, createdByBoclips);
        return Promise.resolve({
            pageSpec: {
                number: page,
                size: size,
                totalElements: filteredCollections.length,
                totalPages: Math.floor(filteredCollections.length / size),
            },
            page: filteredCollections.slice(fromIndex, untilIndex),
        });
    }
    addCommentToCollectionVideo(collection, request) {
        const collectionIndex = this.collections.findIndex((it) => it.id === collection.id);
        const commentObj = {
            id: 'this-is-an-id',
            userId: 'user-id',
            name: request.name,
            email: request.email,
            text: request.text,
            createdAt: Date.now().toString(),
        };
        this.collections[collectionIndex] = {
            ...this.collections[collectionIndex],
            assets: this.collections[collectionIndex].assets.map((asset) => {
                if (asset.id.videoId === request.videoId) {
                    return {
                        ...asset,
                        comments: asset.comments?.concat([commentObj]) || [commentObj],
                    };
                }
                else {
                    return asset;
                }
            }),
        };
        return Promise.resolve(this.collections[collectionIndex]);
    }
    removeCommentFromCollectionVideo(collection, commentId) {
        const collectionIndex = this.collections.findIndex((it) => it.id === collection.id);
        const assetId = this.collections[collectionIndex].assets.find((asset) => asset.comments?.find((comment) => comment.id === commentId))?.id;
        this.collections[collectionIndex] = {
            ...this.collections[collectionIndex],
            assets: this.collections[collectionIndex].assets.map((asset) => {
                if (asset.id === assetId) {
                    const commentIndex = asset.comments?.findIndex((it) => it.id === commentId);
                    return asset.comments && commentIndex !== -1
                        ? {
                            ...asset,
                            comments: asset.comments?.splice(commentIndex),
                        }
                        : asset;
                }
                else {
                    return asset;
                }
            }),
        };
        return Promise.resolve();
    }
    getMyCollections(filter) {
        const page = filter.page || 0;
        const size = filter.size || 10;
        const userCollections = this.collections.filter((c) => c.owner === this.currentUserId || c.mine);
        const fromIndex = page * size;
        const untilIndex = (page + 1) * size;
        return Promise.resolve({
            pageSpec: {
                number: page,
                size: size,
                totalElements: userCollections.length,
                totalPages: Math.floor(userCollections.length / size),
            },
            page: userCollections.slice(fromIndex, untilIndex),
        });
    }
    getMyCollectionsWithoutDetails(filter) {
        return this.getMyCollections(filter).then((result) => this.convertCollectionsToListView(result));
    }
    getMySavedCollections(filter) {
        return this.getCollections(filter, true).then((result) => result);
    }
    getMySavedCollectionsWithoutDetails(filter) {
        return this.getMySavedCollections(filter).then((result) => {
            return this.convertCollectionsToListView(result);
        });
    }
    getMySavedAndEditableCollectionsWithoutDetails(filter) {
        return this.getMySavedCollections({
            canEdit: true,
            ...filter,
        }).then((result) => {
            return this.convertCollectionsToListView(result);
        });
    }
    getMyUserSharedBookmarkedCollections(filter) {
        return this.getCollections(filter, false, false).then((result) => result);
    }
    getMyBoclipsBookmarkedCollections(filter) {
        return this.getCollections(filter, false, true).then((result) => result);
    }
    getPromotedForCollections(filter) {
        return Promise.resolve(this.collections.filter((collection) => collection.promotedFor?.some((it) => it === filter.promotedFor)));
    }
    setCreateCollectionErrorMessage(errorMessage) {
        this.createCollectionErrorMessage = errorMessage;
    }
    create(request) {
        if (typeof this.createCollectionErrorMessage !== 'undefined') {
            return Promise.reject(this.createCollectionErrorMessage);
        }
        const { title, description, discoverable, videos, assets } = request;
        const collection = test_support_1.CollectionFactory.sample({
            id: this.nextId,
            title,
            description,
            assets: assets?.map((assetId) => test_support_1.CollectionAssetFactory.sample({
                id: { videoId: assetId.videoId, highlightId: assetId.highlightId },
                video: VideosFactory_1.VideoWithBoclipsProjectionFactory.sample({
                    id: assetId.videoId,
                }),
            })) ??
                videos?.map((id) => test_support_1.CollectionAssetFactory.sample({
                    id: { videoId: id, highlightId: null },
                    video: VideosFactory_1.VideoWithBoclipsProjectionFactory.sample({ id }),
                })) ??
                [],
            discoverable: discoverable,
            owner: this.currentUserId,
            mine: true,
        });
        this.addToFake(collection);
        return Promise.resolve(collection.id);
    }
    update(id, request) {
        this.collections = this.collections.map((it) => it.id === id
            ? {
                ...it,
                ...FakeCollectionsClient.mapUpdateRequestToFields(request, it.assets),
            }
            : it);
        return Promise.resolve();
    }
    safeUpdate(collection, request) {
        const safeUpdateLink = collection.links.safeEdit?.getOriginalLink();
        if (!safeUpdateLink) {
            throw Error('Not authorized to update collection');
        }
        const lastUpdatedAt = new Date(decodeURIComponent(safeUpdateLink.substring(safeUpdateLink.lastIndexOf('=') + 1)));
        if (collection.updatedAt.getTime() > lastUpdatedAt.getTime()) {
            throw new CollectionUpdateConflictError_1.CollectionUpdateConflictError(collection.id);
        }
        this.collections = this.collections.map((it) => it.id === collection.id
            ? {
                ...it,
                ...FakeCollectionsClient.mapUpdateRequestToFields(request, it.assets),
            }
            : it);
        return Promise.resolve();
    }
    addVideoToCollection(collection, videoId, highlightId = null) {
        const findCollection = this.collections.filter((c) => c.id === collection.id);
        if (findCollection.length === 0) {
            return Promise.reject('Not found');
        }
        const updatedCollection = {
            ...findCollection[0],
            assets: [
                ...findCollection[0].assets,
                {
                    id: { videoId: videoId, highlightId: highlightId },
                    video: VideosFactory_1.VideoFactory.sample({ id: videoId }),
                },
            ],
        };
        this.collections = this.collections.map((it) => it.id === collection.id ? updatedCollection : it);
        return Promise.resolve();
    }
    removeVideoFromCollection(collection, videoId, highlightId = null) {
        const collections = this.collections.filter((c) => c.id === collection.id);
        if (collections.length === 0) {
            return Promise.reject('Not found');
        }
        const updatedCollection = {
            ...collections[0],
            assets: [
                ...collections[0].assets.filter((asset) => asset.id.videoId !== videoId ||
                    asset.id.highlightId !== highlightId),
            ],
        };
        this.collections = this.collections.map((it) => it.id === collection.id ? updatedCollection : it);
        return Promise.resolve();
    }
    bookmark(collection) {
        if (!collection.links.bookmark) {
            return Promise.reject(`Collection ${collection.id} doesn't have a bookmark link`);
        }
        const bookmarkedCollection = {
            ...collection,
            links: {
                ...collection.links,
                unbookmark: new LinkEntity_1.Link({ href: 'something?bookmarked=false' }),
                bookmark: undefined,
            },
        };
        this.collections = this.collections.map((it) => it.id === collection.id ? bookmarkedCollection : it);
        return Promise.resolve(bookmarkedCollection);
    }
    unbookmark(collection) {
        if (!collection.links.unbookmark) {
            return Promise.reject(`Collection ${collection.id} doesn't have an unbookmark link`);
        }
        const unbookmarkedCollection = {
            ...collection,
            links: {
                ...collection.links,
                bookmark: new LinkEntity_1.Link({ href: 'something?bookmarked=true' }),
                unbookmark: undefined,
            },
        };
        this.collections = this.collections.map((it) => it.id === collection.id ? unbookmarkedCollection : it);
        return Promise.resolve(unbookmarkedCollection);
    }
    delete(collectionToRemove) {
        const indexToRemove = this.collections.findIndex((collection) => collection.id === collectionToRemove.id);
        if (indexToRemove !== -1) {
            this.collections.splice(indexToRemove, 1);
        }
        return Promise.resolve();
    }
    addToFake(collection) {
        this.collections.push(collection);
    }
    setNextIdForFake(id) {
        this.nextId = id;
    }
    setCurrentUser(userId) {
        this.currentUserId = userId;
    }
    clear() {
        this.collections = [];
        this.validReferers = [];
    }
    updatePermission(collection, permissions) {
        const index = this.collections.findIndex((it) => it.id === collection.id);
        this.collections[index].permissions = permissions;
        return Promise.resolve();
    }
    addValidReferer(referer) {
        this.validReferers.push(referer);
    }
    getFilteredCollections(query, partialTitleMatch, isOwnCollection, canEdit, createdByBoclips) {
        let filteredCollections = this.collections;
        if (createdByBoclips !== undefined) {
            filteredCollections = filteredCollections.filter((collection) => {
                if (createdByBoclips) {
                    return collection.createdBy === 'Boclips';
                }
                else {
                    return collection.createdBy !== 'Boclips';
                }
            });
        }
        if (isOwnCollection !== undefined) {
            filteredCollections = filteredCollections.filter((collection) => {
                if (isOwnCollection) {
                    return collection.mine || this.isSavedCollection(collection);
                }
                else {
                    return !collection.mine;
                }
            });
        }
        if (canEdit) {
            filteredCollections = filteredCollections.filter((collection) => collection.mine ||
                (this.isSavedCollection(collection) &&
                    collection.permissions.anyone === CollectionPermissions_1.CollectionPermission.EDIT));
        }
        if (query === undefined || query.length == 0)
            return filteredCollections;
        if (partialTitleMatch == true) {
            filteredCollections = filteredCollections.filter((collection) => collection.title.toLowerCase().includes(query.toLowerCase()));
        }
        else {
            filteredCollections = filteredCollections.filter((collection) => collection.title.toLowerCase().includes(query.toLowerCase()) ||
                collection.description?.toLowerCase().includes(query.toLowerCase()));
        }
        return filteredCollections;
    }
    convertCollectionsToListView(collections) {
        return {
            ...collections,
            page: [
                ...collections.page.map((collection) => {
                    return {
                        ...collection,
                        assets: collection.assets.map((asset) => ({ id: asset.id })),
                    };
                }),
            ],
        };
    }
    isSavedCollection(collection) {
        return collection.links.unbookmark && !collection.links.bookmark;
    }
    convertDateIfHasComments(collection) {
        if (collection?.assets !== undefined) {
            collection.assets = collection.assets.map((asset) => {
                asset.comments = asset.comments?.map((comment) => ({
                    ...comment,
                    createdAt: comment.createdAt
                        .substr(0, comment.createdAt.length - 8)
                        .replace('T', ' '),
                }));
                return asset;
            });
        }
        return collection;
    }
}
exports.FakeCollectionsClient = FakeCollectionsClient;
//# sourceMappingURL=FakeCollectionsClient.js.map